import React from 'react';
import PropTypes from 'prop-types';

import { useSprings, animated, interpolate } from 'react-spring';
import { useGesture } from 'react-use-gesture';
import './styles.css';

const to = (i) => ({
  x: 0,
  y: i * -4,
  scale: 1,
  rot: -10 + Math.random() * 20,
  delay: i * 100,
});
const from = (i) => ({ x: 0, rot: 0, scale: 1.5, y: -1000 });
// This is being used down there in the view, it interpolates rotation and scale into a css transform
const trans = (r, s) =>
  `perspective(1500px) rotateX(30deg) rotateY(${
    r / 10
  }deg) rotateZ(${r}deg) scale(${s})`;

const Deck = React.forwardRef(function Deck(props, ref) {
  const { onSwipe, length, renderer, onFinish, style } = props;
  const [gone] = React.useState(() => new Set()); // The set flags all the cards that are flicked out
  const [toView] = React.useState(() => new Set()); // The set flags all the cards that are flicked out
  const [springs, set] = useSprings(length || 1, (i) => ({
    ...to(i),
    from: from(i),
  })); // Create a bunch of springs using the helpers above
  // Create a gesture, we're interested in down-state, delta (current-pos - click-pos), direction and velocity

  React.useImperativeHandle(ref, () => ({
    clear: () => {
      gone.clear();
      toView.clear();
      // setTimeout(() => gone.clear() || set((i) => to(i)), 600);
    },
  }));

  const bind = useGesture(
    ({
      args: [index],
      down,
      delta: [xDelta],
      distance,
      direction: [xDir],
      velocity,
    }) => {
      const trigger = velocity > 0.2; // If you flick hard enough it should trigger the card to fly out
      const dir = xDir < 0 ? -1 : 1; // Direction should either point left or right

      if (!down && trigger) gone.add(index); // If button/finger's up and trigger velocity is reached, we flag the card ready to fly out
      if (!down && trigger && dir > 0) toView.add(index);
      set((i) => {
        if (index !== i) return; // We're only interested in changing spring-data for the current spring
        const isGone = gone.has(index);
        const x = isGone ? (200 + window.innerWidth) * dir : down ? xDelta : 0; // When a card is gone it flys out left or right, otherwise goes back to zero
        const rot = xDelta / 100 + (isGone ? dir * 10 * velocity : 0); // How much the card tilts, flicking it harder makes it rotate faster
        const scale = down ? 1.4 : 1; // Active cards lift up a bit
        if (isGone) {
          onSwipe({ dir, index });
        }
        return {
          x,
          rot,
          scale,
          delay: undefined,
          config: { friction: 50, tension: down ? 800 : isGone ? 200 : 500 },
        };
      });
      if (!down && gone.size === length) {
        onFinish();
      }
    }
  );
  if (length === 0) {
    return null;
  }
  // Now we're just mapping the animated values to our view, that's it. Btw, this component only renders once. :-)
  return (
    <div className={'deck'} style={style}>
      {springs.map(({ x, y, rot, scale }, i) => (
        <animated.div
          key={i}
          style={{
            transform: interpolate(
              [x, y],
              (x, y) => `translate3d(${x}px,${y}px,0)`
            ),
          }}>
          {/* This is the card itself, we're binding our gesture to it (and inject its index so we know which is which) */}
          <animated.div
            {...bind(i)}
            style={{
              transform: interpolate([rot, scale], trans),
            }}>
            {renderer(i)}
          </animated.div>
        </animated.div>
      ))}
    </div>
  );
});

Deck.propTypes = {
  renderer: PropTypes.func,
  onSwipe: PropTypes.func,
  onFinish: PropTypes.func,
  length: PropTypes.number,
  style: PropTypes.object,
};
Deck.defaultProps = {
  cards: [],
  style: {},
  onSwipe: () => {},
  onPress: () => {},
  onFinish: () => {},
  renderer: (i) => {
    return <div>{i}</div>;
  },
};
export default Deck;
